/**
 * Data domains, and the assistants published under each.
 *
 * A data domain is the business audience a context serves, recorded by GenBI
 * Studio and reclassifiable afterwards. An assistant is one published semantic
 * model within that domain: the same governed runtime answers for all of them,
 * and choosing one decides which certified members are in scope.
 *
 * The ids match the values the context layer stores, so a domain and its
 * assistants are matched on data rather than on a name.
 */
export interface ProjectTypeDefinition {
  id: string;
  label: string;
  summary: string;
}

/**
 * The bucket for a context published before project type was recorded.
 *
 * Kept as its own category rather than folded into the three. Spreading these
 * across every category would leave no category ever empty, which hides the
 * fact that nothing has been published for one — and filing them under a
 * guessed type would misrepresent what somebody certified.
 */
export const UNASSIGNED_ID = "unassigned";

export const PROJECT_TYPES: ProjectTypeDefinition[] = [
  {
    id: "service_operation",
    label: "Service Operation",
    summary:
      "Day-to-day delivery and service performance: volumes, handling, "
      + "queues and outcomes.",
  },
  {
    id: "program_management",
    label: "Program Management",
    summary:
      "Programme and project delivery: schedule, milestones, cost and "
      + "utilisation against plan.",
  },
  {
    id: "executive_reporting",
    label: "Executive Reporting",
    summary:
      "Board and leadership reporting: consolidated financials, "
      + "period comparisons and portfolio position.",
  },
  {
    id: "corporate_finance",
    label: "Corporate Finance",
    summary:
      "Financial position and performance: revenue, spend, budget variance "
      + "and capital projects.",
  },
  {
    id: UNASSIGNED_ID,
    label: "Unassigned",
    summary:
      "Published before a project type was recorded. Still certified and "
      + "queryable, just not yet filed under a category.",
  },
];

export function projectTypeFor(id: string | null): ProjectTypeDefinition | null {
  if (!id) return null;
  return PROJECT_TYPES.find((item) => item.id === id) ?? null;
}

export function projectTypeLabel(id: string): string {
  return projectTypeFor(id)?.label ?? "Unassigned";
}

/** The category a published model belongs to, with no bleeding between them. */
export function categoryOf(dataDomain: string | null | undefined): string {
  return dataDomain || UNASSIGNED_ID;
}
